/**
 * UserManagement.jsx
 * Admin panel for managing users within CISO Advisor.
 *
 * Super admins see all users across all customers.
 * Customer admins see only their organization's users.
 *
 * Features:
 *   - List users with role badges
 *   - Create new users
 *   - Edit role and status
 *   - Reset passwords
 *   - Deactivate users
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const ROLES = [
  { value: 'super_admin',    label: 'Super Admin',   color: '#c0392b' },
  { value: 'customer_admin', label: 'Admin',         color: '#2980b9' },
  { value: 'analyst',        label: 'Analyst',       color: '#27ae60' },
  { value: 'read_only',      label: 'Read Only',     color: '#7f8c8d' },
];

const roleFor = (value) => ROLES.find(r => r.value === value) || { label: value, color: '#7f8c8d' };

export default function UserManagement() {
  const { authFetch, currentUser, isSuperAdmin } = useAuth();
  const [users, setUsers]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resetUserId, setResetUserId] = useState(null);

  useEffect(() => { loadUsers(); }, []); // eslint-disable-line

  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await authFetch('/api/auth/users');
      const data = await res.json();
      setUsers(data.users || []);
    } catch (e) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const deactivate = async (userId) => {
    if (!window.confirm('Deactivate this user? They will lose access immediately.')) return;
    await authFetch(`/api/auth/users/${userId}`, { method: 'DELETE' });
    loadUsers();
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>User Management</h1>
          <p style={styles.subtitle}>
            {isSuperAdmin() ? 'All users across all customers' : 'Users in your organization'}
          </p>
        </div>
        <button style={styles.primaryBtn} onClick={() => setShowCreateForm(true)}>
          + Add User
        </button>
      </div>

      {error && <div style={styles.errorBanner}>{error}</div>}

      {loading ? (
        <div style={styles.loading}>Loading users...</div>
      ) : (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                {isSuperAdmin() && <th style={styles.th}>Customer</th>}
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Email</th>
                <th style={styles.th}>Role</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Last Login</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id} style={styles.tr}>
                  {isSuperAdmin() && (
                    <td style={styles.td}>
                      <span style={styles.customerTag}>{user.customer_id || '—'}</span>
                    </td>
                  )}
                  <td style={styles.td}>{user.full_name}</td>
                  <td style={styles.td}>{user.email}</td>
                  <td style={styles.td}>
                    <RoleBadge role={user.role} />
                  </td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.statusBadge,
                      background: user.is_active ? '#1e8449' : '#922b21',
                    }}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={styles.td}>
                    {user.last_login
                      ? new Date(user.last_login).toLocaleDateString()
                      : 'Never'}
                  </td>
                  <td style={styles.td}>
                    <div style={styles.actions}>
                      <button
                        style={styles.actionBtn}
                        onClick={() => setEditingUser(user)}
                      >Edit</button>
                      <button
                        style={styles.actionBtn}
                        onClick={() => setResetUserId(user.id)}
                      >Reset PW</button>
                      {user.id !== currentUser?.id && user.is_active && (
                        <button
                          style={{ ...styles.actionBtn, color: '#e74c3c' }}
                          onClick={() => deactivate(user.id)}
                        >Deactivate</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreateForm && (
        <CreateUserModal
          onClose={() => setShowCreateForm(false)}
          onCreated={() => { setShowCreateForm(false); loadUsers(); }}
          authFetch={authFetch}
          isSuperAdmin={isSuperAdmin()}
          currentCustomerId={currentUser?.customer_id}
        />
      )}

      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSaved={() => { setEditingUser(null); loadUsers(); }}
          authFetch={authFetch}
          isSuperAdmin={isSuperAdmin()}
        />
      )}

      {resetUserId && (
        <ResetPasswordModal
          userId={resetUserId}
          onClose={() => setResetUserId(null)}
          onReset={() => setResetUserId(null)}
          authFetch={authFetch}
        />
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────

function RoleBadge({ role }) {
  const meta = roleFor(role);
  return (
    <span style={{ ...styles.roleBadge, background: meta.color }}>
      {meta.label}
    </span>
  );
}

function CreateUserModal({ onClose, onCreated, authFetch, isSuperAdmin, currentCustomerId }) {
  const [form, setForm] = useState({
    full_name: '', email: '', password: '', role: 'analyst',
    customer_id: currentCustomerId || '',
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const availableRoles = isSuperAdmin ? ROLES : ROLES.filter(r => r.value !== 'super_admin');

  const submit = async () => {
    setSaving(true); setError('');
    try {
      const res = await authFetch('/api/auth/users', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json();
        setError(err.detail || 'Failed to create user');
        return;
      }
      onCreated();
    } catch (e) {
      setError('Network error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Add New User" onClose={onClose}>
      <FormField label="Full Name">
        <input style={styles.input} value={form.full_name}
          onChange={e => setForm({ ...form, full_name: e.target.value })} />
      </FormField>
      <FormField label="Email">
        <input style={styles.input} type="email" value={form.email}
          onChange={e => setForm({ ...form, email: e.target.value })} />
      </FormField>
      <FormField label="Temporary Password">
        <input style={styles.input} type="password" value={form.password}
          onChange={e => setForm({ ...form, password: e.target.value })} />
      </FormField>
      {isSuperAdmin && (
        <FormField label="Customer ID">
          <input style={styles.input} value={form.customer_id}
            onChange={e => setForm({ ...form, customer_id: e.target.value })}
            placeholder="cust_acmefinanc_abc123" />
        </FormField>
      )}
      <FormField label="Role">
        <select style={styles.input} value={form.role}
          onChange={e => setForm({ ...form, role: e.target.value })}>
          {availableRoles.map(r => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
      </FormField>
      {error && <p style={styles.errorText}>{error}</p>}
      <div style={styles.modalActions}>
        <button style={styles.secondaryBtn} onClick={onClose}>Cancel</button>
        <button style={styles.primaryBtn} onClick={submit} disabled={saving}>
          {saving ? 'Creating...' : 'Create User'}
        </button>
      </div>
    </Modal>
  );
}

function EditUserModal({ user, onClose, onSaved, authFetch, isSuperAdmin }) {
  const [form, setForm] = useState({
    full_name: user.full_name,
    role: user.role,
    is_active: user.is_active,
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const availableRoles = isSuperAdmin ? ROLES : ROLES.filter(r => r.value !== 'super_admin');

  const submit = async () => {
    setSaving(true); setError('');
    try {
      const res = await authFetch(`/api/auth/users/${user.id}`, {
        method: 'PUT',
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json();
        setError(err.detail || 'Failed to update user');
        return;
      }
      onSaved();
    } catch { setError('Network error'); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={`Edit: ${user.full_name}`} onClose={onClose}>
      <FormField label="Full Name">
        <input style={styles.input} value={form.full_name}
          onChange={e => setForm({ ...form, full_name: e.target.value })} />
      </FormField>
      <FormField label="Role">
        <select style={styles.input} value={form.role}
          onChange={e => setForm({ ...form, role: e.target.value })}>
          {availableRoles.map(r => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
      </FormField>
      <FormField label="Status">
        <select style={styles.input} value={form.is_active ? 'active' : 'inactive'}
          onChange={e => setForm({ ...form, is_active: e.target.value === 'active' })}>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </FormField>
      {error && <p style={styles.errorText}>{error}</p>}
      <div style={styles.modalActions}>
        <button style={styles.secondaryBtn} onClick={onClose}>Cancel</button>
        <button style={styles.primaryBtn} onClick={submit} disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </Modal>
  );
}

function ResetPasswordModal({ userId, onClose, onReset, authFetch }) {
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [saving, setSaving]     = useState(false);

  const submit = async () => {
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setSaving(true); setError('');
    try {
      const res = await authFetch(`/api/auth/users/${userId}/reset-password`, {
        method: 'POST',
        body: JSON.stringify({ new_password: password }),
      });
      if (!res.ok) {
        const err = await res.json();
        setError(err.detail || 'Failed to reset password');
        return;
      }
      onReset();
    } catch { setError('Network error'); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="Reset Password" onClose={onClose}>
      <p style={{ color: '#bdc3c7', marginBottom: '1rem' }}>
        Enter a temporary password. The user should change it after logging in.
      </p>
      <FormField label="New Password">
        <input style={styles.input} type="password" value={password}
          onChange={e => setPassword(e.target.value)} placeholder="Min 8 characters" />
      </FormField>
      {error && <p style={styles.errorText}>{error}</p>}
      <div style={styles.modalActions}>
        <button style={styles.secondaryBtn} onClick={onClose}>Cancel</button>
        <button style={styles.primaryBtn} onClick={submit} disabled={saving}>
          {saving ? 'Resetting...' : 'Reset Password'}
        </button>
      </div>
    </Modal>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>{title}</h2>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <label style={styles.label}>{label}</label>
      {children}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────

const styles = {
  container: { padding: '2rem', background: '#1a1a2e', minHeight: '100vh', color: '#ecf0f1' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2rem' },
  title: { fontSize: '1.8rem', fontWeight: 700, color: '#ecf0f1', margin: 0 },
  subtitle: { color: '#7f8c8d', marginTop: '0.25rem', fontSize: '0.9rem' },
  loading: { textAlign: 'center', color: '#7f8c8d', padding: '3rem' },
  errorBanner: { background: '#922b21', color: 'white', padding: '0.75rem 1rem', borderRadius: 6, marginBottom: '1rem' },
  tableWrapper: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '0.75rem 1rem', color: '#7f8c8d', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #2c3e50' },
  tr: { borderBottom: '1px solid #1e2d40' },
  td: { padding: '0.85rem 1rem', color: '#ecf0f1', fontSize: '0.9rem' },
  roleBadge: { display: 'inline-block', padding: '0.25rem 0.6rem', borderRadius: 4, fontSize: '0.75rem', fontWeight: 600, color: 'white' },
  statusBadge: { display: 'inline-block', padding: '0.25rem 0.6rem', borderRadius: 4, fontSize: '0.75rem', color: 'white' },
  customerTag: { fontFamily: 'monospace', fontSize: '0.8rem', color: '#7f8c8d' },
  actions: { display: 'flex', gap: '0.5rem' },
  actionBtn: { background: 'none', border: '1px solid #2c3e50', color: '#bdc3c7', padding: '0.25rem 0.6rem', borderRadius: 4, cursor: 'pointer', fontSize: '0.8rem' },
  primaryBtn: { background: '#2980b9', color: 'white', border: 'none', padding: '0.6rem 1.25rem', borderRadius: 6, cursor: 'pointer', fontWeight: 600 },
  secondaryBtn: { background: 'transparent', color: '#bdc3c7', border: '1px solid #2c3e50', padding: '0.6rem 1.25rem', borderRadius: 6, cursor: 'pointer' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#16213e', border: '1px solid #2c3e50', borderRadius: 12, padding: '2rem', width: '100%', maxWidth: 480 },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' },
  modalTitle: { fontSize: '1.2rem', fontWeight: 600, color: '#ecf0f1', margin: 0 },
  closeBtn: { background: 'none', border: 'none', color: '#7f8c8d', fontSize: '1.1rem', cursor: 'pointer' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem' },
  label: { display: 'block', color: '#bdc3c7', fontSize: '0.85rem', marginBottom: '0.4rem' },
  input: { width: '100%', padding: '0.6rem 0.75rem', background: '#0f3460', border: '1px solid #2c3e50', borderRadius: 6, color: '#ecf0f1', fontSize: '0.9rem', boxSizing: 'border-box' },
  errorText: { color: '#e74c3c', fontSize: '0.85rem', marginTop: '0.5rem' },
};
